<script setup lang="ts">
import NounInflectionSchemeModal from "@/components/article/dictsys/inflectionSchemes/german/NounInflectionSchemeModal.vue";
import InflectionSchemeCellPlaceholder from "@/components/inflectionTrainer/InflectionSchemeCellPlaceholder.vue";
import { makeInflectionCell } from "@/helpers/dictsys-inflection-scheme/inflection-scheme.helper";
import type {
  DictsysInflectionSchemeGermanNounCase,
  DictsysInflectionSchemeGermanNounGenders
} from "@/types";

interface Affix {
  prefix: string;
  suffix: string;
}

const HAS_SUFFIX = "HAS_SUFFIX";

const makeInflectionScheme = (
  singular: Affix,
  plural: Affix
): DictsysInflectionSchemeGermanNounCase => ({
  singular: makeInflectionCell("", singular.prefix, singular.suffix),
  plural: makeInflectionCell("", plural.prefix, plural.suffix)
});

const inflectionScheme: DictsysInflectionSchemeGermanNounGenders = {
  neutral: {
    definite: {
      nominative: makeInflectionScheme(
        { prefix: "das", suffix: "" },
        { prefix: "die", suffix: HAS_SUFFIX }
      ),
      akkusative: makeInflectionScheme(
        { prefix: "das", suffix: "" },
        { prefix: "die", suffix: HAS_SUFFIX }
      ),
      genitive: makeInflectionScheme(
        { prefix: "des", suffix: HAS_SUFFIX },
        { prefix: "der", suffix: HAS_SUFFIX }
      ),
      dative: makeInflectionScheme(
        { prefix: "dem", suffix: "" },
        { prefix: "den", suffix: HAS_SUFFIX }
      )
    },
    indefinite: {
      nominative: makeInflectionScheme(
        { prefix: "ein", suffix: "" },
        { prefix: "keine", suffix: HAS_SUFFIX }
      ),
      akkusative: makeInflectionScheme(
        { prefix: "ein", suffix: "" },
        { prefix: "keine", suffix: HAS_SUFFIX }
      ),
      genitive: makeInflectionScheme(
        { prefix: "eines", suffix: HAS_SUFFIX },
        { prefix: "keiner", suffix: HAS_SUFFIX }
      ),
      dative: makeInflectionScheme(
        { prefix: "einem", suffix: "" },
        { prefix: "keinen", suffix: HAS_SUFFIX }
      )
    }
  },
  masculine: {
    definite: {
      nominative: makeInflectionScheme(
        { prefix: "der", suffix: "" },
        { prefix: "die", suffix: HAS_SUFFIX }
      ),
      akkusative: makeInflectionScheme(
        { prefix: "den", suffix: "" },
        { prefix: "die", suffix: HAS_SUFFIX }
      ),
      genitive: makeInflectionScheme(
        { prefix: "des", suffix: HAS_SUFFIX },
        { prefix: "der", suffix: HAS_SUFFIX }
      ),
      dative: makeInflectionScheme(
        { prefix: "dem", suffix: "" },
        { prefix: "den", suffix: HAS_SUFFIX }
      )
    },
    indefinite: {
      nominative: makeInflectionScheme(
        { prefix: "ein", suffix: "" },
        { prefix: "keine", suffix: HAS_SUFFIX }
      ),
      akkusative: makeInflectionScheme(
        { prefix: "einen", suffix: "" },
        { prefix: "keine", suffix: HAS_SUFFIX }
      ),
      genitive: makeInflectionScheme(
        { prefix: "eines", suffix: HAS_SUFFIX },
        { prefix: "keiner", suffix: HAS_SUFFIX }
      ),
      dative: makeInflectionScheme(
        { prefix: "einem", suffix: "" },
        { prefix: "keinen", suffix: HAS_SUFFIX }
      )
    }
  },
  feminine: {
    definite: {
      nominative: makeInflectionScheme(
        { prefix: "die", suffix: "" },
        { prefix: "die", suffix: HAS_SUFFIX }
      ),
      akkusative: makeInflectionScheme(
        { prefix: "die", suffix: "" },
        { prefix: "die", suffix: HAS_SUFFIX }
      ),
      genitive: makeInflectionScheme(
        { prefix: "der", suffix: "" },
        { prefix: "der", suffix: HAS_SUFFIX }
      ),
      dative: makeInflectionScheme(
        { prefix: "der", suffix: "" },
        { prefix: "den", suffix: HAS_SUFFIX }
      )
    },
    indefinite: {
      nominative: makeInflectionScheme(
        { prefix: "eine", suffix: "" },
        { prefix: "keine", suffix: HAS_SUFFIX }
      ),
      akkusative: makeInflectionScheme(
        { prefix: "eine", suffix: "" },
        { prefix: "keine", suffix: HAS_SUFFIX }
      ),
      genitive: makeInflectionScheme(
        { prefix: "einer", suffix: "" },
        { prefix: "keiner", suffix: HAS_SUFFIX }
      ),
      dative: makeInflectionScheme(
        { prefix: "einer", suffix: "" },
        { prefix: "keinen", suffix: HAS_SUFFIX }
      )
    }
  }
};
</script>

<template>
  <NounInflectionSchemeModal :inflection-scheme="inflectionScheme">
    <template #inflection-cell="{ inflections }">
      <InflectionSchemeCellPlaceholder
        v-if="inflections.length > 0"
        :inflection="inflections[0]"
        use-suffix-box
      />
    </template>
  </NounInflectionSchemeModal>
</template>
